import * as React from 'react';
import { echoService } from '@/services/echoService';
import { MessageSender } from '@/types';
import type { Message } from '@/types';

interface UseDraftChatReturn {
  messages: Message[];
  isLoading: boolean;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
}

/**
 * Manages chat state for a draft echo's test session.
 *
 * Text streams via /test-chat-stream (text-only). When generateAudio is true and
 * the draft has at least one VoiceClone, the AI reply is post-synthesized via the
 * existing /voice-clone/{cloneId}/test endpoint (returns MP3 Blob), turned into a
 * blob URL, and attached to the message as audio_url so MessageActions renders the
 * play button.
 *
 * @param draftId Draft echo identifier.
 * @param greetings Pre-configured greetings; one is shown as the first AI message.
 * @param generateAudio When true, request voice-clone TTS for AI replies.
 */
export const useDraftChat = (
  draftId: string,
  greetings: string[],
  generateAudio = false
): UseDraftChatReturn => {
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);
  const greetingSentRef = React.useRef(false);
  const voiceCloneIdRef = React.useRef<string | null>(null);
  const blobUrlsRef = React.useRef<string[]>([]);

  React.useEffect(() => {
    if (greetingSentRef.current) return;
    greetingSentRef.current = true;

    const validGreetings = greetings.filter((greeting) => greeting.trim());
    if (validGreetings.length === 0) return;
    const greeting = validGreetings[Math.floor(Math.random() * validGreetings.length)] ?? '';

    const greetingMsg: Message = {
      id: `greeting-${Date.now()}`,
      content: greeting,
      sender_type: MessageSender.AI,
      created_at: new Date().toISOString(),
      isTextComplete: true,
      isAllAudioStreamed: true,
    };
    setMessages([greetingMsg]);
  }, [greetings]);

  /** Lazy-resolve and cache the draft's most recent voice clone id (backend returns desc by created_at). */
  const resolveVoiceCloneId = React.useCallback(async (): Promise<string | null> => {
    if (voiceCloneIdRef.current) return voiceCloneIdRef.current;
    try {
      const { voice_clones } = await echoService.listVoiceClones(draftId);
      const preferred = voice_clones?.[0];
      if (!preferred) return null;
      voiceCloneIdRef.current = preferred.id;
      return preferred.id;
    } catch {
      return null;
    }
  }, [draftId]);

  const attachAudioToMessage = React.useCallback(
    async (messageId: string, text: string) => {
      const cloneId = await resolveVoiceCloneId();
      if (!cloneId) return;
      try {
        const blob = await echoService.testVoiceClone(draftId, cloneId, text);
        const url = URL.createObjectURL(blob);
        blobUrlsRef.current.push(url);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? { ...msg, audio_url: url, has_audio: true, isAudioGenerated: true }
              : msg
          )
        );
      } catch {
        // Voice clone test failed; leave message text-only, no toast (silent fallback).
      }
    },
    [draftId, resolveVoiceCloneId]
  );

  const sendMessage = React.useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      const userMsg: Message = {
        id: `user-${Date.now()}`,
        content,
        sender_type: MessageSender.USER,
        created_at: new Date().toISOString(),
        isTextComplete: true,
      };

      const aiTempId = `ai-${Date.now()}`;
      const aiMsg: Message = {
        id: aiTempId,
        content: '',
        sender_type: MessageSender.AI,
        created_at: new Date().toISOString(),
        isTextComplete: false,
        isAllAudioStreamed: true,
      };

      setMessages((prev) => [...prev, userMsg, aiMsg]);
      setIsLoading(true);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      let finalText = '';

      try {
        await echoService.streamDraftTestChat(
          draftId,
          content,
          (event, data) => {
            if (event === 'text_chunk') {
              const chunk = data['content'] as string;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiTempId ? { ...msg, content: msg.content + chunk } : msg
                )
              );
            } else if (event === 'message_end') {
              finalText = (data['content'] as string) ?? '';
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiTempId ? { ...msg, content: finalText, isTextComplete: true } : msg
                )
              );
              setIsLoading(false);
            } else if (event === 'error') {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiTempId
                    ? {
                        ...msg,
                        content: 'Something went wrong. Please try again.',
                        isTextComplete: true,
                      }
                    : msg
                )
              );
              setIsLoading(false);
            }
          },
          controller.signal
        );
      } catch {
        if (!controller.signal.aborted) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiTempId
                ? { ...msg, content: 'Connection failed. Please try again.', isTextComplete: true }
                : msg
            )
          );
          setIsLoading(false);
        }
        return;
      }

      if (generateAudio && finalText && !controller.signal.aborted) {
        void attachAudioToMessage(aiTempId, finalText);
      }
    },
    [draftId, isLoading, generateAudio, attachAudioToMessage]
  );

  const clearMessages = React.useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setIsLoading(false);
    greetingSentRef.current = false;
  }, []);

  React.useEffect(
    () => () => {
      abortRef.current?.abort();
      blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      blobUrlsRef.current = [];
    },
    []
  );

  return { messages, isLoading, sendMessage, clearMessages };
};
